import React, { useState, useRef } from "react";
import { Facebook, Instagram, Twitter, ChevronRight, ChevronDown, Award, Truck, Headphones, Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useContact } from "./context/ContactContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useCategories } from "./context/CategoriesContext";
import { getCategoryUrl } from "../utils/productUrl";

const Footer = () => {
  const { contactData } = useContact();
  const navigate = useNavigate();
  const location = useLocation();
  const { categories, loading, error } = useCategories();
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const categoriesScrollRef = useRef(null);

  const handleNavigation = (path, itemKey) => {
    // Set highlight
    setHighlightedItem(itemKey);

    // Check if we're already on the target page
    if (location.pathname === path) {
      // If on the same page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If not on the same page, navigate to the page
      navigate(path);
    }

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItem(null);
    }, 2000);
  };

  const handleLogoClick = () => {
    // Set highlight
    setHighlightedItem("logo");

    // Check if already on home page
    if (location.pathname === "/") {
      // If on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If not on home page, navigate to home
      navigate("/");
    }

    // Remove highlight after 2 seconds
    setTimeout(() => {
      setHighlightedItem(null);
    }, 2000);
  };

  const handleCategoryClick = (id) => {
    const cat = categories.find(c => String(c.id) === String(id));
    navigate(cat ? getCategoryUrl(cat) : '/shop');
  };

  const handleViewAllClick = () => {
    navigate('/shop');
  };

  // Toggle section expansion for mobile/tablet
  const toggleSection = (sectionIndex) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  // Drag scroll functionality for categories
  const handleMouseDown = (e) => {
    const slider = categoriesScrollRef.current;
    if (!slider) return;

    const startX = e.pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    const onMove = (e) => {
      const x = e.pageX - slider.offsetLeft;
      slider.scrollLeft = scrollLeft - (x - startX) * 2;
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener(
      "mouseup",
      () => document.removeEventListener("mousemove", onMove),
      { once: true }
    );
  };

  // Get shop links from actual categories with enhanced functionality
  const getShopLinks = () => {
    if (loading || !categories.length) {
      return [
        { label: "T-Shirts", path: "/products", key: "tshirts" },
        { label: "Mugs", path: "/products", key: "mugs" },
        { label: "Wall Photo Frames", path: "/products", key: "frames" },
        { label: "Photo Stands", path: "/products", key: "stands" },
      ];
    }

    // Use first 4 categories from the API
    return categories.slice(0, 4).map((category) => ({
      label: category.title,
      path: `/category/${category.id}`,
      key: `category-${category.id}`,
      id: category.id,
    }));
  };

  const footerSections = [
    {
      title: "Best Sellers",
      links: getShopLinks(),
      isCategories: true,
    },
    {
      title: "Company",
      links: [
        { label: "About Us", path: "/about", key: "about" },
        { label: "Contact Us", path: "/contact", key: "contact" },
        { label: "FAQs", path: "/faq", key: "faq" },
        { label: "Blog", path: "/blog", key: "blog" },
      ],
    },
    {
      title: "Others",
      links: [
        { label: "Terms & Conditions", path: "/terms&conditions", key: "terms" },
        { label: "Returns & Refunds", path: "/refund-policy", key: "refunds" },
        { label: "Privacy Policy", path: "/privacy-policy", key: "privacy" },
        { label: "Payment Policy", path: "/payment-policy", key: "payment" },
      ],
    },
  ];

  const features = [
    {
      icon: Award,
      title: "Premium Quality",
      description: "High-quality materials and printing",
    },
    {
      icon: Truck,
      title: "Fast Delivery",
      description: "Quick and reliable shipping",
    },
    {
      icon: Headphones,
      title: "24/7 Support",
      description: "Always here to help you",
    },
  ];

  const paymentMethods = [
    {
      name: "UPI",
      icon: "https://cdn.acsdev.in/ManaPrint/bhim-upi-icon.png",
    },
    {
      name: "Mastercard",
      icon: "https://cdn.acsdev.in/ManaPrint/MasterCard_Logo.svg.png",
    },
    {
      name: "Maestro",
      icon: "https://cdn.acsdev.in/ManaPrint/Maestro_Logo.svg",
    },
    {
      name: "Visa",
      icon: "https://cdn.acsdev.in/ManaPrint/visa.png",
    },
    {
      name: "Paytm",
      icon: "https://cdn.acsdev.in/ManaPrint/paytm.png",
    },
    {
      name: "PhonePe",
      icon: "https://cdn.acsdev.in/ManaPrint/phonepe.png",
    },
  ];

  const getSocialLinks = () => {
    if (!contactData) return [];

    return [
      {
        icon: Facebook,
        name: "Facebook",
        href: contactData.facebook?.startsWith("http")
          ? contactData.facebook
          : `https://${contactData.facebook}`,
        show: contactData.facebook && contactData.facebook !== "#",
      },
      {
        icon: Instagram,
        name: "Instagram",
        href: contactData.instagram?.startsWith("http")
          ? contactData.instagram
          : `https://${contactData.instagram}`,
        show: contactData.instagram && contactData.instagram !== "#",
      },
      {
        icon: Twitter,
        name: "Twitter",
        href: contactData.twitter?.startsWith("http")
          ? contactData.twitter
          : `https://${contactData.twitter}`,
        show: contactData.twitter && contactData.twitter !== "#",
      },
    ].filter((social) => social.show);
  };

  const handlePhoneClick = () => {
    if (contactData?.phone) {
      window.open(`tel:${contactData.phone.replace(/\s+/g, "")}`, "_blank");
    }
  };

  const handleWhatsAppClick = () => {
    if (contactData?.phone) {
      const phoneNumber = contactData.phone.replace(/\D/g, "");
      const message = "Hi, I would like to inquire about your services.";
      window.open(
        `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
        "_blank",
      );
    }
  };

  const socialLinks = getSocialLinks();

  return (
    <footer className="w-full bg-black mt-2" role="contentinfo">
      <div className="w-full mx-auto">
        {/* Features Section - Redesigned */}
        <section
          className="px-[100px] py-3 max-sm:px-5 max-sm:py-8"
          aria-labelledby="features-heading"
        >
          <h2 id="features-heading" className="sr-only">
            Our Features
          </h2>
          <div className="flex justify-between items-center max-w-7xl mx-auto max-md:flex-row max-md:gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center max-md:flex-col max-md:items-center max-md:gap-3"
              >
                <div className="p-4 bg-white bg-opacity-10 rounded-full mb-4 max-md:mb-0 max-md:p-3">
                  <feature.icon
                    className="w-8 h-8 text-white max-md:w-6 max-md:h-6"
                    aria-hidden="true"
                  />
                </div>
                <div className="max-md:text-center">
                  <h3 className="text-lg font-bold text-white mb-2 max-md:mb-0 max-md:text-base">
                    {feature.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Divider Line */}
        <div className="w-full h-px bg-gray-600" role="separator"></div>

        {/* Social Media, Logo, and Contact Section */}
        <section
          className="px-[60px] py-3 max-sm:px-5 max-sm:py-4"
          aria-labelledby="social-contact-heading"
        >
          <h2 id="social-contact-heading" className="sr-only">
            Social Media, Company Info, and Contact
          </h2>
          <div className="flex justify-between items-center max-lg:flex-col max-lg:gap-8">
            {/* Left: Social Media */}
            <div className="flex items-center gap-6 max-lg:order-2">
              <span className="text-white font-medium">Follow Us:</span>
              <div
                className="flex gap-4"
                role="list"
                aria-label="Social media links"
              >
                {socialLinks.map((social, index) => {
                  const IconComponent = social.icon;
                  return (
                    <a
                      key={index}
                      href={social.href}
                      className="p-2 hover:bg-gray-800 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20"
                      aria-label={`Visit our ${social.name} page`}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="listitem"
                    >
                      <IconComponent className="w-8 h-8 text-white" />
                    </a>
                  );
                })}
              </div>
            </div>

            {/* Center: Logo and Description */}
            <div className="flex flex-col items-center text-center space-y-4 px-8 max-lg:order-1">
              <div className="flex-shrink-0">
                <img
                  onClick={handleLogoClick}
                  src="https://cdn.acsdev.in/ManaPrint/manaprint_new.png"
                  alt="Manaprint Logo"
                  className="h-16 w-auto object-contain rounded-2xl cursor-pointer hover:opacity-80 transition-opacity"
                />
              </div>
              <p className="text-sm font-light text-white leading-relaxed max-w-xl">
                Your one-stop shop for custom prints, apparel, gifts, and
                business branding. Bringing your ideas to life—one print at a
                time.
              </p>
            </div>

            {/* Right: Contact */}
            <div className="flex items-center gap-4 max-lg:order-3">
              <span className="text-white font-medium"> Contact Us:</span>
              <div
                className="flex gap-3"
                role="list"
                aria-label="Contact options"
              >
                <button
                  onClick={handlePhoneClick}
                  className="p-2 hover:bg-gray-800 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20"
                  aria-label="Call us"
                  disabled={!contactData?.phone}
                  role="listitem"
                >
                  <Phone className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={handleWhatsAppClick}
                  className="p-2 hover:bg-gray-800 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-20"
                  aria-label="Contact us on WhatsApp"
                  disabled={!contactData?.phone}
                  role="listitem"
                >
                  <FaWhatsapp className="w-10 h-10 text-white" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Divider Line */}
        <div className="w-full h-px bg-gray-600" role="separator"></div>

        {/* Secure Payments Section */}
        <section
          className="px-[100px] py-4 max-sm:px-5 max-sm:py-4"
          aria-labelledby="payments-heading"
        >
          <h2 id="payments-heading" className="sr-only">
            Secure Payment Methods
          </h2>
          <div className="flex flex-col items-center text-center space-y-4">
            <h3 className="text-lg font-bold text-white">100% SECURE PAYMENTS</h3>
            <div className="grid grid-cols-3 gap-4 w-full max-w-sm mx-auto md:flex md:flex-wrap md:justify-between md:items-center md:max-w-7xl md:gap-0">
              {paymentMethods.map((method, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg px-3 py-2 shadow-sm hover:scale-105 transition flex justify-center items-center"
                >
                  <img
                    src={method.icon}
                    alt={method.name}
                    className="h-8 w-auto scale-110 object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Divider Line */}
        <div className="w-full h-px bg-gray-600" role="separator"></div>

        {/* Main Footer Links */}
        <section className="px-6 sm:px-12 lg:px-24 py-8" aria-labelledby="main-footer-heading">
          <h2 id="main-footer-heading" className="sr-only">Footer Navigation</h2>

          <div className="bg-orange-50 rounded-2xl px-6 py-6 max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {footerSections.map((section, sectionIndex) => (
                <div key={sectionIndex}>
                  {/* Title */}
                  <div className="flex items-center justify-between mb-3">
                    <h4
                      className="text-xs font-bold uppercase tracking-widest text-gray-800 cursor-pointer md:cursor-default w-full text-center"
                      onClick={() => toggleSection(sectionIndex)}
                    >
                      {section.title}
                    </h4>
                    <button
                      className="sm:hidden text-gray-500 flex-shrink-0"
                      onClick={() => toggleSection(sectionIndex)}
                      aria-label={`Toggle ${section.title}`}
                    >
                      {expandedSections[sectionIndex]
                        ? <ChevronDown className="w-4 h-4" />
                        : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Links */}
                  <nav className={`flex flex-col items-center gap-2 sm:flex ${expandedSections[sectionIndex] ? 'flex' : 'hidden'}`}>
                    {section.isCategories && loading && (
                      <span className="text-xs text-gray-400">Loading…</span>
                    )}
                    {section.links.map((link, linkIndex) => (
                      <button
                        key={linkIndex}
                        onClick={() =>
                          section.isCategories && link.id
                            ? handleCategoryClick(link.id)
                            : handleNavigation(link.path, link.key)
                        }
                        className={`text-center text-sm font-semibold transition-colors duration-150 ${
                          highlightedItem === link.key
                            ? 'text-emerald-700'
                            : 'text-gray-600 hover:text-emerald-700'
                        }`}
                      >
                        {link.label}
                      </button>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Copyright */}
        <div className="border-t border-gray-800">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-8 space-y-4 sm:space-y-0 px-24 py-4 max-sm:px-5">
            <p className="text-sm text-gray-400">
              ©2026 Manaprint. All rights reserved. Designed & Developed by{" "}
              <span
                onClick={() =>
                  window.open("https://algorithmicaconsulting.com/", "_blank")
                }
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                Algorithmica Consultancy Services Pvt Ltd
              </span>
              .
            </p>
            <div className="flex  gap-6 text-sm text-gray-400">
              <button
                onClick={() => handleNavigation("/privacy-policy", "footer-privacy")}
                className={`hover:text-white transition-colors duration-200 ${
                  highlightedItem === "footer-privacy" ? 'text-white' : ''
                }`}
              >
                Privacy Policy
              </button>
              <button
                onClick={() => handleNavigation("/terms&conditions", "footer-terms")}
                className={`hover:text-white transition-colors duration-200 ${
                  highlightedItem === "footer-terms" ? 'text-white' : ''
                }`}
              >
                Terms & Conditions
              </button>
              <button
                onClick={() => handleNavigation("/cookie-policy", "footer-cookies")}
                className={`hover:text-white transition-colors duration-200 ${
                  highlightedItem === "footer-cookies" ? 'text-white' : ''
                }`}
              >
                Cookie Policy
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        /* Hide scrollbar */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .animate-spin {
            animation: none;
          }

          .transition-all {
            transition: none;
          }
        }

        /* Smooth accordion animation */
        @media (max-width: 768px) {
          .accordion-content {
            overflow: hidden;
            transition: max-height 0.3s ease-in-out;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
